import type {
    GroupData,
    OptionData,
    SelectData,
} from "@@/carts/deliverypicker/select-picker-native";
import { SelectPickerNative } from "@@/carts/deliverypicker/select-picker-native";
import { useBookingContext } from "@@/products/bookables/booking-context";
import { Conditional } from "@@/shared/conditional";
import { HorizontalDivider } from "@@/shared/dividers";
import { TextBox } from "@@/shared/text";
import { useDateTimeFormatter } from "@@/shared/use-date-time-formatter";
import { useTranslate } from "@@/translations/use-translate";
import { useTheme } from "@emotion/react";
import type {
    Bookable,
    BookableSession,
    Price,
    TimeRange,
    TimeRangeResourceAvailability,
    Translatable,
} from "@towni/common";
import {
    asDate,
    calculateSpanningTimeRanges,
    inHours,
    inMinutes,
    isoAndUnixFactory,
    translation,
} from "@towni/common";
import { isSameDay, isToday, isTomorrow, startOfDay } from "date-fns"; // Added startOfDay, isAfter
import * as React from "react";

/**
 * Helper function to format the display name for a time range option.
 */
const formatTimeRangeOptionName = (
    timeRange: TimeRange,
    bookable: Bookable,
    durationGranularity: string,
    formatter: ReturnType<typeof useDateTimeFormatter>,
    translate: ReturnType<typeof useTranslate>,
): Translatable => {
    const start = asDate(timeRange.start);
    const end = asDate(timeRange.end);

    if (durationGranularity === "HOUR") {
        const sameDay = isSameDay(start, end);
        const spanning = bookable.type === "RENTAL" && !!bookable.spanning;

        // Handle same day non-spanning bookings
        if (sameDay && !spanning) {
            const hours = inHours(
                { seconds: timeRange.duration },
                { noRounding: true },
            ).toLocaleString();
            return translation({
                sv: `${hours} h`,
                en: `${hours} h`,
            });
        } else {
            // For multi-day or spanning bookings
            const formattedEnd = translate(
                formatter.formatRelativeDateMedium(timeRange.end, {
                    includeTime: true,
                }),
            );
            return translation({
                sv: `${formattedEnd}`,
                en: `${formattedEnd}`,
            });
            //Testing to hude duration
            // const duration = getDurationString(start, end);

            // return translation({
            //     sv: `${formattedEnd} (${duration})`,
            //     en: `${formattedEnd} (${duration})`,
            // });
        }
    }

    // Handle minute granularity (default case)
    const minutes = inMinutes(
        { seconds: timeRange.duration },
        { noRounding: true },
    ).toLocaleString();
    return translation({
        sv: `${minutes} min`,
        en: `${minutes} min`,
    });
};

// Props definition remains the same
type Props = {
    selectedSession: BookableSession;
    readonly onDurationChange: (timeRange: TimeRange) => void;
    readonly checkAvailability: (
        timeRange: TimeRange,
    ) => TimeRangeResourceAvailability;
    readonly ranges: TimeRange[];
    currentPrice: (session: BookableSession, timeRange?: TimeRange) => Price;
};

const DurationPicker = (props: Props) => {
    const { onDurationChange, checkAvailability, ranges, selectedSession } =
        props;
    const theme = useTheme();
    const formatter = useDateTimeFormatter();
    const translate = useTranslate();
    const contextTimeRange = useBookingContext(state => state.timeRange);
    const bookable = useBookingContext(state => state.bookable);
    const sessions = useBookingContext(state => state.sessions);

    const [selectedDuration, setSelectedDuration] = React.useState<
        TimeRange | undefined
    >(contextTimeRange);

    const timeRangeResourceAvailability = React.useMemo(() => {
        let possibleTimeRanges: TimeRange[] = [];
        if (bookable.type === "RENTAL" && bookable.spanning) {
            possibleTimeRanges = calculateSpanningTimeRanges(
                selectedSession,
                sessions,
            );
        } else {
            possibleTimeRanges = ranges;
        }
        return possibleTimeRanges.map(timeRange =>
            checkAvailability(timeRange),
        );
    }, [bookable, selectedSession, sessions, ranges, checkAvailability]);

    React.useEffect(() => {
        const firstAvailable = timeRangeResourceAvailability.find(
            availability =>
                availability.resourcesAvailability.every(
                    resource => resource.available,
                ),
        );
        if (firstAvailable && selectedDuration === undefined) {
            setSelectedDuration(firstAvailable.timeRange);
            onDurationChange(firstAvailable.timeRange);
        }
        const currentSelectionStillAvailable =
            selectedDuration &&
            timeRangeResourceAvailability.some(
                av =>
                    av.timeRange._id === selectedDuration._id &&
                    av.resourcesAvailability.every(r => r.available),
            );
        if (selectedDuration && !currentSelectionStillAvailable) {
            // Handle deselection if needed
        }
    }, [onDurationChange, selectedDuration, timeRangeResourceAvailability]);

    // --- Generate data with optgroups ---
    const data: SelectData<string | number | undefined> = React.useMemo(() => {
        const availableOptions = timeRangeResourceAvailability
            .filter(availability =>
                availability.resourcesAvailability.every(
                    resource => resource.available,
                ),
            )
            // Sort by start time primarily, maybe end time secondarily if needed
            .sort(
                (a, b) =>
                    asDate(a.timeRange.start).getTime() -
                    asDate(b.timeRange.start).getTime(),
            );

        // --- Grouping Logic ---
        const groupedData: SelectData<string | number | undefined> = [];
        let lastEndDay: Date | null = null;
        let currentGroup: GroupData<string | number | undefined> | null = null;

        availableOptions.forEach(({ timeRange }) => {
            const currentEndDay = startOfDay(asDate(timeRange.end));

            // Format the option itself
            const optionItem: OptionData<string | number | undefined> = {
                name: formatTimeRangeOptionName(
                    timeRange,
                    bookable,
                    props.selectedSession.durationGranularity,
                    formatter,
                    translate,
                ),
                value: timeRange._id,
            };

            // If day changed (or first item), create a new group
            if (!lastEndDay || !isSameDay(currentEndDay, lastEndDay)) {
                const today = isToday(currentEndDay);
                const tomorrow = isTomorrow(currentEndDay);
                let groupLabel: Translatable | string;

                if (today) {
                    groupLabel = translate(
                        translation({ sv: "Idag", en: "Today" }),
                    );
                } else if (tomorrow) {
                    groupLabel = translate(
                        translation({ sv: "Imorgon", en: "Tomorrow" }),
                    );
                } else {
                    // Format other days (ensure isoAndUnixFactory is needed or simplify)
                    groupLabel = translate(
                        formatter.formatRelativeDateMedium(
                            isoAndUnixFactory(currentEndDay),
                            // Or potentially just: currentEndDay
                        ),
                    );
                }

                // Create new group and add it to the result array
                currentGroup = {
                    isGroup: true,
                    label: groupLabel,
                    options: [optionItem],
                };
                groupedData.push(currentGroup);
                lastEndDay = currentEndDay; // Update the last day tracker
            } else if (currentGroup) {
                // If same day, add the option to the existing group's options array
                // (currentGroup cannot be null here due to the logic flow)
                currentGroup.options.push(optionItem);
            }
        });

        return groupedData;
    }, [
        timeRangeResourceAvailability,
        bookable,
        props.selectedSession.durationGranularity,
        formatter,
        translate,
    ]);
    // --- End data generation ---

    if (!selectedDuration) return null;

    return (
        <>
            <Conditional when={bookable.type === "RENTAL" && bookable.spanning}>
                <TextBox text={translation({ sv: "Till ", en: "Until " })} />
                <HorizontalDivider XS />
            </Conditional>
            {/* Use the SelectPicker component that supports optgroups */}
            <SelectPickerNative<string | number | undefined>
                // Pass the structured data
                data={data}
                selectedValue={selectedDuration._id}
                onSelect={({ value: timeRangeId }) => {
                    // This logic remains the same - find the TimeRange by its ID
                    const available = timeRangeResourceAvailability.find(
                        item => item.timeRange._id === timeRangeId,
                    );
                    if (available) {
                        setSelectedDuration(available.timeRange);
                        props.onDurationChange(available.timeRange);
                    }
                }}
                css={{
                    borderRadius: `${theme.radius}px 0 0 ${theme.radius}px`,
                }}
            />
        </>
    );
};

export { DurationPicker };
